import type { FormConfig } from "~/components/form-engine";

export const adminOnboardingConfig: FormConfig = {
  id: "sat-onboarding",
  title: "SAT Connection Setup",
  description: "Connect your company to the SAT invoice portal",
  submitButtonText: "Complete Setup",
  successMessage: "Your SAT connection has been configured successfully!",
  steps: [
    {
      id: "welcome",
      title: "Welcome",
      description: "Get started with SAT connection setup",
      icon: "shield",
      fields: [
        {
          name: "startOnboarding",
          label: "Start Setup",
          type: "checkbox",
          description: "I understand that I will need my RFC, company information, and SAT portal credentials to complete this setup.",
          validation: {
            required: true,
          },
        },
      ],
    },
    {
      id: "company-verification",
      title: "Company Verification",
      description: "Verify your company information",
      icon: "briefcase",
      fields: [
        {
          name: "rfc",
          label: "RFC",
          type: "text",
          placeholder: "Completa tu RFC",
          description: "Completa tu RFC. Los primeros caracteres se muestran como referencia.",
          sensitive: true, // SECURITY: Prevent storage in localStorage
          validation: {
            required: true,
            minLength: 12,
            maxLength: 13,
            pattern: "^[A-Za-zÑñ&]{3,4}\\d{6}[A-Za-z0-9]{3}$",
            patternMessage: "onboarding.validation.invalidRfc",
          },
        },
        {
          name: "razonSocial",
          label: "Razón Social",
          type: "text",
          placeholder: "Completa la razón social",
          description: "Completa la razón social de tu empresa. Los primeros caracteres se muestran como referencia.",
          sensitive: true, // SECURITY: Prevent storage in localStorage
          validation: {
            required: true,
            minLength: 3,
            maxLength: 150,
          },
        },
        {
          name: "regimenFiscal",
          label: "Régimen Fiscal",
          type: "select",
          placeholder: "Select tax regime",
          description: "Select your tax regime as registered with SAT",
          validation: {
            required: true,
          },
          options: [
            { value: "601", label: "601 - General de Ley Personas Morales" },
            { value: "603", label: "603 - Personas Morales con Fines no Lucrativos" },
            { value: "605", label: "605 - Sueldos y Salarios e Ingresos Asimilados a Salarios" },
            { value: "606", label: "606 - Arrendamiento" },
            { value: "607", label: "607 - Régimen de Enajenación o Adquisición de Bienes" },
            { value: "608", label: "608 - Demás ingresos" },
            { value: "610", label: "610 - Residentes en el Extranjero sin Establecimiento Permanente en México" },
            { value: "611", label: "611 - Ingresos por Dividendos (socios y accionistas)" },
            { value: "612", label: "612 - Personas Físicas con Actividades Empresariales y Profesionales" },
            { value: "614", label: "614 - Ingresos por intereses" },
            { value: "615", label: "615 - Régimen de los ingresos por obtención de premios" },
            { value: "616", label: "616 - Sin obligaciones fiscales" },
            { value: "620", label: "620 - Sociedades Cooperativas de Producción que optan por diferir sus ingresos" },
            { value: "621", label: "621 - Incorporación Fiscal" },
            { value: "622", label: "622 - Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras" },
            { value: "623", label: "623 - Opcional para Grupos de Sociedades" },
            { value: "624", label: "624 - Coordinados" },
            { value: "625", label: "625 - Régimen de las Actividades Empresariales con ingresos a través de Plataformas Tecnológicas" },
            { value: "626", label: "626 - Régimen Simplificado de Confianza" },
          ],
        },
      ],
    },
    {
      id: "sat-credentials",
      title: "SAT Portal Credentials",
      description: "Enter your SAT portal access credentials",
      icon: "lock",
      disablePersistence: true,
      fields: [
        {
          name: "cedulaFiscal",
          label: "Cédula de Identificación Fiscal",
          type: "file",
          description: "Upload your Cédula Fiscal (PDF only, max 2MB)",
          validation: {
            required: true,
          },
          accept: ".pdf",
          maxFileSize: 2097152,
        },
        {
          name: "satUsername",
          label: "SAT Portal Username",
          type: "text",
          placeholder: "Enter your SAT username",
          description: "Your username for the SAT invoice portal",
          sensitive: true,
          validation: {
            required: true,
            minLength: 3,
            maxLength: 50,
          },
        },
        {
          name: "satPassword",
          label: "SAT Portal Password",
          type: "password",
          placeholder: "Enter your SAT password",
          showPasswordToggle: true,
          sensitive: true,
          description: "Your password for the SAT invoice portal",
          validation: {
            required: true,
            minLength: 3,
            maxLength: 14,
          },
        },
        {
          name: "confirmSatPassword",
          label: "Confirm SAT Password",
          type: "password",
          placeholder: "Re-enter your SAT password",
          showPasswordToggle: true,
          sensitive: true,
          validation: {
            required: true,
            minLength: 3,
            maxLength: 14,
            matchField: "satPassword",
          },
        },
      ],
    },
    {
      id: "review",
      title: "Review & Confirm",
      description: "Review your information and accept the terms",
      icon: "file-check",
      fields: [
        {
          name: "acceptTerms",
          label: "I accept the Terms of Service and Privacy Policy, and authorize the connection to the SAT portal.",
          type: "checkbox",
          validation: {
            required: true,
          },
        },
        {
          name: "acceptDataProcessing",
          label: "I consent to the secure processing of my SAT credentials for invoice management purposes.",
          type: "checkbox",
          validation: {
            required: true,
          },
        },
        {
          name: "acknowledgeResponsibility",
          label: "I understand that I am responsible for keeping my SAT credentials secure and up to date.",
          type: "checkbox",
          validation: {
            required: true,
          },
        },
      ],
    },
  ],
};
